// src/components/layout/PersonalDataForm.tsx
import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPen } from '@/icons';
import { apiGet } from '../../services/api';
import { useFormFormatting } from '../../hooks/useFormFormatting';
import { Pessoa } from '../../types/usuarios/Pessoa';
import { formatWhatsapp, formatCpf } from '../../utils/formatUtils';
import '../../components/layout/PersonalDataForm.css';
import CancelButton from '../common/CancelButton';
import SaveButton from '../common/SaveButton';
import { useApiCall } from '../../hooks/useApiCall';
import { showAlert } from '../../contexts/DialogContext';
import { Toast } from '../../contexts/ToastContext';
import { logger } from '@/utils/logger';

interface PersonalDataFormProps {
  theme: 'dark' | 'light';
  onClose: () => void;
}

const PersonalDataForm: React.FC<PersonalDataFormProps> = ({ onClose }) => {
  const { callApi: callApiSave, isLoading: isSaving } = useApiCall();
  const [initialData, setInitialData] = useState<Pessoa | null>(null);

  const { fields, errors, handleChange, isFormValid, setFields } = useFormFormatting(initialData);

  useEffect(() => {
    const fetchPersonalData = async () => {
      try {
        const res = await apiGet('/v1/pessoas?dados-pessoais=true');
        logger.log('Resposta do backend:', res.data); 
        const data: Pessoa = res.data;

        const formattedWhatsapp = formatWhatsapp(data.whatsapp || data.contato?.whatsapp || '');
        const formattedCpf = formatCpf(data.cpf);

        setInitialData(data);
        setFields({
          nome: data.nome,
          cpf: formattedCpf,
          whatsapp: formattedWhatsapp,
          email: data.email || data.contato?.email || '',
          userName: data.userName || data.usuario?.userName || '',
        });
      } catch (err) {
        logger.error('Erro ao carregar dados pessoais:', err);
        await showAlert('Erro ao carregar dados pessoais.', { variant: 'error' });
      }
    };
    fetchPersonalData();
  }, [setFields]);

  const handleSave = async () => {
    if (!isFormValid()) {
      await showAlert('Corrija os campos inválidos antes de salvar!', { variant: 'warning' });
      return;
    }

    const updatedData = {
      nome: fields.nome,
      whatsapp: fields.whatsapp,
      email: fields.email,
    };

    try {
      await callApiSave('put', '/v1/pessoas/dados-pessoais', updatedData, {
        onSuccess: () => {
          Toast.success('Dados pessoais atualizados com sucesso!');
          onClose();
        },
        onError: (error: any) => {
          const errorMessage = error.response?.data?.error || 'Erro ao atualizar dados pessoais';
          showAlert(errorMessage, { variant: 'error' });
        },
      });
    } catch (err) {
      logger.error('Erro ao salvar dados pessoais:', err);
    }
  }; // Refatorado para useApiCall, removendo setIsLoading manual.

  return (
    <div className="form-container">
      <h2><FontAwesomeIcon icon={faUserPen} /> Editar Dados Pessoais</h2>
      {initialData ? (
        <div>
          <label className="form-label">
            Nome:
            <input
              type="text"
              value={fields.nome}
              onChange={(e) => handleChange('nome', e.target.value)}
              className={`form-input ${errors.nome ? 'input-error' : ''}`}
            />
            {errors.nome && <span className="error-message">{errors.nome}</span>}
          </label>
          <label className="form-label">
            CPF:
            <input
              type="text"
              value={fields.cpf}
              disabled
              className="form-input"
            />
            {errors.cpf && <span className="error-message">{errors.cpf}</span>}
          </label>
          <label className="form-label">
            Whatsapp:
            <input
              type="text"
              value={fields.whatsapp}
              onChange={(e) => handleChange('whatsapp', e.target.value)}
              className={`form-input ${errors.whatsapp ? 'input-error' : ''}`}
            />
            {errors.whatsapp && <span className="error-message">{errors.whatsapp}</span>}
          </label>
          <label className="form-label">
            Email:
            <input
              type="email"
              value={fields.email}
              disabled
              onChange={(e) => handleChange('email', e.target.value)}
              className={`form-input ${errors.email ? 'input-error' : ''}`}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </label>
          <label className="form-label">
            UserName:
            <input
              type="text"
              value={fields.userName}
              disabled
              className="form-input"
            />
            {errors.userName && <span className="error-message">{errors.userName}</span>}
          </label>
          <div className="button-container">
            <SaveButton
              isLoading={isSaving}
              onClick={handleSave}
              disabled={!isFormValid() || isSaving}
            />
            <CancelButton onClick={onClose} disabled={isSaving} /> {/* Desabilita durante salvamento. */}
          </div>
        </div>
      ) : (
        <p>Carregando...</p>
      )}
    </div>
  );
};

export default PersonalDataForm;